import styled from "@emotion/styled";
import { Drawer as MuiDrawer, Typography, drawerClasses } from "@mui/material";
import React from "react";
import useDrawer from "./useDrawer";
import { useIsMobile, useStatusBarDisplayed } from "@hooks";
import CloseIcon from "src/icons/CloseIcon";
import ErrorCircleIcon from "src/icons/ErrorCircleIcon";
import { APP_HEADER_HEIGHT, APP_MOBILE_HEADER_HEIGHT, APP_STATUS_BAR_HEIGHT } from "src/layout/consts";
import { HyroButton, HyroIconButton } from "@hyro-components";
import { BUTTON_COLORS, BUTTON_VARIANTS } from "@hyro-components/hyro-button/HyroButton.consts";
import { ICON_BUTTON_SIZES, ICON_BUTTON_VARIANTS } from "@hyro-components/hyro-icon-button/HyroIconButton.consts";

const BottomContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: auto;
  padding: 18px 24px;
  border-top: 1px solid ${({ theme, isContentOverflowing }) =>
    isContentOverflowing
      ? theme.palette.system[100]
      : "transparent"};
`;

const DrawerError = styled(Typography)`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.palette.error[500]};
`;

const ContentContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 24px;
  gap: 6px;
  height: 100%;
  max-height: fit-content;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 8px;
    background-color: transparent;
  }
;

  &::-webkit-scrollbar-thumb {
    background-color: #DBDFE9;
    border-radius: 16px;
  }
;
`;

const Drawer = (props) => {
  const { isOpen, isSubmitLoading, onClose, onSubmit, isSubmitDisabled, showSubmit = true, children, generalError, cancelText = "Cancel", disableCloseInteractions, testId } = props;
  const showStatusBar = useStatusBarDisplayed();
  const isMobile = useIsMobile();

  const { DRAWER_CONTENT_ID, isContentOverflowing } = useDrawer(children);
  const appHeader = isMobile ? APP_MOBILE_HEADER_HEIGHT : APP_HEADER_HEIGHT;

  return (
    <MuiDrawer
      open={isOpen}
      anchor={"right"}
      sx={{
        [`.${drawerClasses.paper}`]:
         {
           width: "100%",
           maxWidth: "548px",
           top: appHeader + (showStatusBar ? APP_STATUS_BAR_HEIGHT : 0),
           height: `calc(100% - ${appHeader + (showStatusBar ? APP_STATUS_BAR_HEIGHT : 0)}px)`
         }
      }}
      onClose={disableCloseInteractions ? undefined : onClose}
    >
      <ContentContainer id={DRAWER_CONTENT_ID}>
        <HyroIconButton
          sx={{ position: "absolute", top: 24, right: 24 }}
          data-testid="close-drawer-button"
          variant={ICON_BUTTON_VARIANTS.CLEAR}
          size={ICON_BUTTON_SIZES.LARGE}
          onClick={onClose}>
          <CloseIcon />
        </HyroIconButton>
        {children}
      </ContentContainer>
      <BottomContainer isContentOverflowing={isContentOverflowing}>
        {generalError ?
          <DrawerError variant="caption">
            <ErrorCircleIcon />
            {generalError}
          </DrawerError> : <div />}
        <div style={{ display: "flex", gap: "8px" }}>
          <HyroButton
            data-testid={`${testId ? `${testId}-` : ""}close-button`}
            variant={showSubmit ? BUTTON_VARIANTS.TEXT : BUTTON_VARIANTS.CONTAINED}
            color={showSubmit ? BUTTON_COLORS.SECONDARY : BUTTON_COLORS.PRIMARY}
            onClick={onClose}>
            {cancelText}
          </HyroButton>

          {showSubmit && <HyroButton
            data-testid='drawer-submit-button'
            variant={BUTTON_VARIANTS.CONTAINED}
            color={BUTTON_COLORS.PRIMARY}
            onClick={onSubmit}
            loading={isSubmitLoading}
            disabled={isSubmitDisabled}
          >
            Submit
          </HyroButton>}
        </div>
      </BottomContainer>
    </MuiDrawer>
  );
};

export default Drawer;
